/**
 * ImagesTab — V1 Lean Engine image gallery for a content package.
 *
 * Shows the 9 image assets (1 article infographic + 8 Pinterest pins).
 * Each card supports: preview, view full size, download, approve,
 * reject, regenerate, copy URL.
 *
 * Polls the progress endpoint every 1.5s while a generation job is
 * running so the admin sees a 0-100% bar fill in real time.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Loader2,
    RefreshCw,
    Check,
    X,
    Download,
    Copy,
    ExternalLink,
    Image as ImageIcon,
    AlertCircle,
    CheckCircle2,
    Sparkles,
    PlayCircle,
    Send,
    CheckCheck,
    FlaskConical,
} from "lucide-react";
import {
    adminListPackageImages,
    adminImageProgress,
    adminGenerateAllImages,
    adminRegenerateImageAsset,
    adminApproveImageAsset,
    adminRejectImageAsset,
    adminBulkApproveImages,
    adminMetricoolStatus,
    adminSendPackageToPinterest,
    adminMetricoolTestPin,
    adminGetPinSettings,
    adminSetPinBoard,
} from "@/lib/api";
import { toast } from "sonner";

const ASSET_LABELS = {
    article_infographic: { label: "Article Infographic", angle: "Infographic" },
    pinterest_educational_guide: { label: "Pin · Educational Guide", angle: "Day 1" },
    pinterest_cost_repair_exposure: { label: "Pin · Cost / Repair Exposure", angle: "Day 3" },
    pinterest_checklist: { label: "Pin · Checklist", angle: "Day 5" },
    pinterest_warning_signs: { label: "Pin · Warning Signs", angle: "Day 8" },
    pinterest_calculator_tool: { label: "Pin · Calculator / Tool", angle: "Day 11" },
    pinterest_negotiation_seller_credit: { label: "Pin · Negotiation / Seller Credit", angle: "Day 15" },
    pinterest_decision_walk_away: { label: "Pin · Decision / Walk Away", angle: "Day 20" },
    pinterest_inspection_report_wording: { label: "Pin · Report Wording", angle: "Day 27" },
};

// Mirror of backend `_LEGACY_TO_CANONICAL` in metricool_routes.py — used
// to map historic pin.key values to the canonical asset_type slug.
const LEGACY_TO_CANONICAL = {
    educational: "educational_guide",
    cost: "cost_repair_exposure",
    checklist: "checklist",
    warning_signs: "warning_signs",
    calculator: "calculator_tool",
    negotiation: "negotiation_seller_credit",
    walk_away: "decision_walk_away",
    report_wording: "inspection_report_wording",
};

function _legacyToCanonical(key) {
    return LEGACY_TO_CANONICAL[key] || key;
}

const STATUS_STYLES = {
    pending: { dot: "bg-bl-muted/40", label: "Pending", tone: "text-bl-muted" },
    generating: { dot: "bg-blue-500 animate-pulse", label: "Generating…", tone: "text-blue-600" },
    generated: { dot: "bg-[var(--bl-green)]", label: "Generated", tone: "text-[var(--bl-green)]" },
    approved: { dot: "bg-[var(--bl-teal)]", label: "Approved", tone: "text-bl-primary" },
    rejected: { dot: "bg-[var(--bl-rust)]", label: "Rejected", tone: "text-[var(--bl-rust)]" },
    failed: { dot: "bg-[var(--bl-rust)]", label: "Failed", tone: "text-[var(--bl-rust)]" },
};

function ProgressBar({ progress }) {
    if (!progress || progress.status === "idle") return null;
    const pct = Math.max(0, Math.min(100, progress.percent || 0));
    const done = progress.status === "succeeded" || progress.status === "partial_success" || progress.status === "failed";
    return (
        <div
            className="bl-card p-4 space-y-2"
            data-testid="image-progress-bar"
        >
            <div className="flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 font-medium text-bl">
                    {done ? (
                        progress.status === "succeeded" ? (
                            <CheckCircle2 className="h-4 w-4 text-[var(--bl-green)]" />
                        ) : (
                            <AlertCircle className="h-4 w-4 text-[var(--bl-amber)]" />
                        )
                    ) : (
                        <Loader2 className="h-4 w-4 text-bl-primary animate-spin" />
                    )}
                    <span>
                        {done
                            ? progress.status === "succeeded"
                                ? "All 9 images generated"
                                : progress.status === "partial_success"
                                    ? "Generation completed with errors"
                                    : "Image generation failed"
                            : "Generating images…"}
                    </span>
                </div>
                <span className="text-bl-muted font-mono" data-testid="image-progress-percent">{pct}%</span>
            </div>
            <div className="h-2 bg-bl-bg rounded-full overflow-hidden border border-bl/30">
                <div
                    className="h-full bg-bl-primary transition-all duration-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="text-[11px] text-bl-muted">
                {progress.current_step || "—"} · {progress.completed || 0} / {progress.total || 9}
            </div>
        </div>
    );
}

function ImageCard({
    asset, packageId, onChange,
    pinMeta, boards, defaultBoardKey, currentBoardKey, onBoardChange,
}) {
    const [busy, setBusy] = useState(false);
    const meta = ASSET_LABELS[asset.asset_type] || { label: asset.asset_type, angle: "" };
    const status = STATUS_STYLES[asset.generation_status] || STATUS_STYLES.pending;
    // v5.02 — append `?v={updated_at}` so the browser treats the
    // image as a fresh URL after regenerate. Backend caches the
    // stable path for 60 s (see `serve_image` headers); this
    // query-param version bump immediately invalidates the admin
    // gallery's cached bytes so the newly-regenerated pin shows
    // up instantly instead of the pre-regeneration snapshot.
    const versionTag = encodeURIComponent(
        asset.updated_at || asset.generated_at || "",
    );
    const url = asset.public_url
        ? (asset.public_url.startsWith("http")
            ? `${asset.public_url}${versionTag ? (asset.public_url.includes("?") ? "&" : "?") + "v=" + versionTag : ""}`
            : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${asset.public_url}${versionTag ? `?v=${versionTag}` : ""}`)
        : null;
    const hasImage = !!url && (asset.generation_status === "generated" || asset.generation_status === "approved");

    const handleRegen = useCallback(async () => {
        if (busy || !asset.asset_id) return;
        setBusy(true);
        try {
            const r = await adminRegenerateImageAsset(packageId, asset.asset_id);
            if (r.success) {
                toast.success("Image regenerated");
            } else {
                toast.error(`Regenerate failed: ${r.error || "unknown"}`);
            }
            await onChange();
        } catch (e) {
            toast.error(`Regenerate failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [busy, asset.asset_id, packageId, onChange]);

    const handleApprove = useCallback(async () => {
        if (busy || !asset.asset_id) return;
        setBusy(true);
        try {
            await adminApproveImageAsset(packageId, asset.asset_id);
            toast.success("Approved");
            await onChange();
        } catch (e) {
            toast.error(`Approve failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [busy, asset.asset_id, packageId, onChange]);

    const handleReject = useCallback(async () => {
        if (busy || !asset.asset_id) return;
        setBusy(true);
        try {
            await adminRejectImageAsset(packageId, asset.asset_id);
            toast.success("Rejected");
            await onChange();
        } catch (e) {
            toast.error(`Reject failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [busy, asset.asset_id, packageId, onChange]);

    const handleCopyUrl = useCallback(() => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        toast.success("URL copied");
    }, [url]);

    const handleTestPin = useCallback(async () => {
        if (busy || !pinMeta?.key) return;
        setBusy(true);
        const t = toast.loading(`Testing pin "${meta.label}" on Metricool…`);
        try {
            const r = await adminMetricoolTestPin(packageId, pinMeta.key);
            toast.dismiss(t);
            if (r.ok) {
                toast.success(
                    `Dry-run passed · board=${r.internal_board_key} · scheduled+deleted in Metricool`,
                );
            } else if (r.metricool_post_id && !r.deleted) {
                toast.warning(
                    `Pin scheduled (id=${r.metricool_post_id}) but cleanup failed — delete it manually in Metricool. ${r.delete_error || ""}`,
                );
            } else {
                toast.error("Dry-run failed — see backend logs");
            }
        } catch (e) {
            toast.dismiss(t);
            toast.error(`Test failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [busy, pinMeta?.key, packageId, meta]);

    return (
        <div
            className="bl-card p-3 flex flex-col gap-3"
            data-testid={`image-card-${asset.asset_type}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-xs font-semibold text-bl truncate">{meta.label}</div>
                    {meta.angle && (
                        <div className="text-[10px] uppercase tracking-wider text-bl-muted">
                            {meta.angle}
                        </div>
                    )}
                </div>
                <div className={`flex items-center gap-1.5 text-[11px] ${status.tone}`}>
                    <span className={`h-2 w-2 rounded-full ${status.dot}`} />
                    <span data-testid={`image-status-${asset.asset_type}`}>{status.label}</span>
                </div>
            </div>

            {/* Preview area */}
            <div
                className="relative aspect-[2/3] rounded border border-bl/30 bg-bl-bg overflow-hidden flex items-center justify-center"
                data-testid={`image-preview-${asset.asset_type}`}
            >
                {hasImage ? (
                    <img
                        src={url}
                        alt={meta.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : asset.generation_status === "failed" ? (
                    <div className="p-3 text-center text-[11px]">
                        <AlertCircle className="h-6 w-6 text-[var(--bl-rust)] mx-auto mb-1" />
                        <div className="font-medium text-[var(--bl-rust)] mb-1">Generation failed</div>
                        <div className="text-bl-muted leading-tight">{(asset.error_message || "").slice(0, 120)}</div>
                    </div>
                ) : asset.generation_status === "generating" ? (
                    <div className="text-center text-[11px] text-bl-muted">
                        <Loader2 className="h-6 w-6 text-bl-primary mx-auto mb-1 animate-spin" />
                        Rendering…
                    </div>
                ) : (
                    <div className="text-center text-[11px] text-bl-muted/70">
                        <ImageIcon className="h-6 w-6 mx-auto mb-1 opacity-40" />
                        Pending
                    </div>
                )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-1.5">
                {hasImage && (
                    <>
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                            data-testid={`image-view-${asset.asset_type}`}
                        >
                            <ExternalLink className="h-3 w-3" /> View
                        </a>
                        <a
                            href={url}
                            download={`${asset.file_name || asset.asset_type}.png`}
                            className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                            data-testid={`image-download-${asset.asset_type}`}
                        >
                            <Download className="h-3 w-3" /> Download
                        </a>
                        <button
                            type="button"
                            onClick={handleCopyUrl}
                            className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                            data-testid={`image-copy-${asset.asset_type}`}
                        >
                            <Copy className="h-3 w-3" /> Copy URL
                        </button>
                    </>
                )}
                {asset.asset_id && asset.generation_status !== "generating" && (
                    <button
                        type="button"
                        onClick={handleRegen}
                        disabled={busy}
                        className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                        data-testid={`image-regen-${asset.asset_type}`}
                    >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Regenerate
                    </button>
                )}
                {hasImage && asset.generation_status === "generated" && (
                    <button
                        type="button"
                        onClick={handleApprove}
                        disabled={busy}
                        className="bl-btn-primary text-[10px] gap-1 px-2 py-1"
                        data-testid={`image-approve-${asset.asset_type}`}
                    >
                        <Check className="h-3 w-3" /> Approve
                    </button>
                )}
                {hasImage && asset.generation_status !== "rejected" && (
                    <button
                        type="button"
                        onClick={handleReject}
                        disabled={busy}
                        className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                        data-testid={`image-reject-${asset.asset_type}`}
                    >
                        <X className="h-3 w-3" /> Reject
                    </button>
                )}
                {hasImage && pinMeta?.key && (
                    <button
                        type="button"
                        onClick={handleTestPin}
                        disabled={busy}
                        title="Schedule + auto-delete this pin in Metricool to verify image, board + title without publishing"
                        className="bl-btn-ghost text-[10px] gap-1 px-2 py-1"
                        data-testid={`image-testpin-${asset.asset_type}`}
                    >
                        {busy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <FlaskConical className="h-3 w-3" />
                        )}
                        Test pin
                    </button>
                )}
            </div>
            {pinMeta && (
                <PinSeoMeta
                    pinMeta={pinMeta}
                    boards={boards}
                    defaultBoardKey={defaultBoardKey}
                    currentBoardKey={currentBoardKey}
                    onBoardChange={onBoardChange}
                    angleKey={pinMeta.key}
                />
            )}
        </div>
    );
}

function PinSeoMeta({
    pinMeta, boards, defaultBoardKey, currentBoardKey, onBoardChange, angleKey,
}) {
    // Board the pin will actually ship to: override if set, else the
    // angle's canonical default.
    const effectiveBoardKey = currentBoardKey || defaultBoardKey;
    return (
        <div
            className="border-t border-bl/20 pt-2 mt-1 space-y-2"
            data-testid={`pin-meta-${angleKey}`}
        >
            <div>
                <div className="text-[9px] uppercase tracking-wider text-bl-muted">SEO Title</div>
                <div
                    className="text-[11px] text-bl font-medium leading-snug line-clamp-2"
                    title={pinMeta.seo_title || pinMeta.pin_title}
                    data-testid={`pin-seo-title-${angleKey}`}
                >
                    {pinMeta.seo_title || pinMeta.pin_title || "—"}
                </div>
            </div>
            <div>
                <div className="text-[9px] uppercase tracking-wider text-bl-muted">Description</div>
                <div
                    className="text-[11px] text-bl-muted leading-snug line-clamp-3"
                    title={pinMeta.pin_description}
                    data-testid={`pin-description-${angleKey}`}
                >
                    {pinMeta.pin_description || "—"}
                </div>
            </div>
            <div>
                <div className="text-[9px] uppercase tracking-wider text-bl-muted">Link</div>
                <a
                    href={pinMeta.destination_url || pinMeta.utm_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-bl-primary hover:underline truncate block"
                    data-testid={`pin-link-${angleKey}`}
                >
                    {pinMeta.destination_url || pinMeta.utm_url || "—"}
                </a>
            </div>
            <div>
                <div className="text-[9px] uppercase tracking-wider text-bl-muted">Board</div>
                <select
                    value={effectiveBoardKey || ""}
                    onChange={(e) => onBoardChange?.(angleKey, e.target.value)}
                    className="w-full text-[11px] border border-bl/30 rounded px-1.5 py-1 bg-bl-bg text-bl"
                    data-testid={`pin-board-select-${angleKey}`}
                >
                    {(boards || []).map((b) => (
                        <option key={b.key} value={b.key}>
                            {b.name}{b.key === defaultBoardKey ? "  (default)" : ""}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <div className="text-[9px] uppercase tracking-wider text-bl-muted">Tags</div>
                <div
                    className="flex flex-wrap gap-1"
                    data-testid={`pin-tags-${angleKey}`}
                >
                    {(pinMeta.hashtags || []).length === 0 ? (
                        <span className="text-[10px] text-bl-muted">—</span>
                    ) : (
                        (pinMeta.hashtags || []).map((tag, i) => (
                            <span
                                key={i}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-bl-primary/10 text-bl-primary"
                            >
                                #{tag}
                            </span>
                        ))
                    )}
                </div>
            </div>
            {pinMeta.primary_keyword && (
                <div className="text-[10px] text-bl-muted">
                    Primary keyword: <span className="font-mono text-bl">{pinMeta.primary_keyword}</span>
                </div>
            )}
        </div>
    );
}

export default function ImagesTab({ packageId, pkg }) {
    const [data, setData] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [metricoolStatus, setMetricoolStatus] = useState(null);
    const [sending, setSending] = useState(false);
    const [bulkApproving, setBulkApproving] = useState(false);
    const [lastSendResult, setLastSendResult] = useState(null);
    const [pinSettings, setPinSettings] = useState(null);
    const pollRef = useRef(null);

    // Build a quick lookup: asset_type → pin meta (so we can render
    // SEO title / description / link / tags on each pin card).
    const pinsByAssetType = useMemo(() => {
        const pins = ((pkg?.social_assets || {}).pinterest || {}).pins || [];
        const out = {};
        for (const p of pins) {
            const angleKey = p.canonical_angle_key || _legacyToCanonical(p.key) || p.key;
            const assetType = `pinterest_${angleKey}`;
            out[assetType] = p;
        }
        return out;
    }, [pkg]);

    const handlePinBoardChange = useCallback(
        async (angleKey, boardKey) => {
            try {
                await adminSetPinBoard(packageId, angleKey, boardKey);
                // Optimistic local update so the dropdown reflects immediately.
                setPinSettings((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        pin_overrides: {
                            ...(prev.pin_overrides || {}),
                            [angleKey]: { board_key: boardKey },
                        },
                    };
                });
                toast.success("Board assignment updated.");
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail || "Failed to update board.",
                );
            }
        },
        [packageId],
    );

    // Fetch on mount + whenever `reloadKey` ticks. Set-state happens
    // inside the async IIFE (after the first await) which is the
    // pattern the rest of the admin UI uses to satisfy the React
    // Compiler's `set-state-in-effect` rule.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminListPackageImages(packageId);
                if (!alive) return;
                setData(r);
                setProgress(r.progress);
            } catch (e) {
                if (alive) {
                    toast.error(`Failed to load images: ${e?.response?.data?.detail || e.message}`);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [packageId, reloadKey]);

    // Load Metricool config + per-pin settings once on mount.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const s = await adminMetricoolStatus();
                if (alive) setMetricoolStatus(s);
            } catch {
                if (alive) setMetricoolStatus({ configured: false });
            }
            try {
                const ps = await adminGetPinSettings(packageId);
                if (alive) setPinSettings(ps);
            } catch {
                /* pin settings are optional — no toast */
            }
        })();
        return () => { alive = false; };
    }, [packageId]);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    // Poll progress while a generation job is in flight. Started from
    // the click handler (and re-attached on remount-into-running) so
    // we never call setState synchronously from inside an effect.
    const startPolling = useCallback(() => {
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            try {
                const p = await adminImageProgress(packageId);
                setProgress(p);
                if (p.status === "succeeded" || p.status === "partial_success" || p.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setGenerating(false);
                    setReloadKey((k) => k + 1);
                }
            } catch {
                /* swallow; next poll retries */
            }
        }, 1500);
    }, [packageId]);

    // Cleanup polling on unmount.
    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, []);

    // Resume polling if we open a tab whose package is mid-generation.
    // Pure side-effect — no React state writes inside the effect body.
    useEffect(() => {
        if (progress?.status === "running" && !pollRef.current) {
            startPolling();
        }
    }, [progress?.status, startPolling]);

    const handleGenerateAll = useCallback(async ({ force = false } = {}) => {
        setGenerating(true);
        try {
            await adminGenerateAllImages(packageId, { force });
            toast.success("Image generation queued");
            // Immediately refresh; the worker will start within ~2s and
            // the progress poller will take over from there.
            setReloadKey((k) => k + 1);
            startPolling();
        } catch (e) {
            setGenerating(false);
            toast.error(`Enqueue failed: ${e?.response?.data?.detail || e.message}`);
        }
    }, [packageId, startPolling]);

    const handleSendToPinterest = useCallback(async () => {
        if (sending) return;
        setSending(true);
        setLastSendResult(null);
        try {
            const r = await adminSendPackageToPinterest(packageId);
            setLastSendResult(r);
            if (r.ok) {
                const skipped = r.skipped_already_scheduled || 0;
                if (skipped > 0) {
                    toast.success(`${r.scheduled} scheduled, ${skipped} already on the Metricool calendar`);
                } else {
                    toast.success(`Scheduled all ${r.total} pins on Pinterest via Metricool`);
                }
            } else {
                toast.warning(`Scheduled ${r.scheduled} of ${r.total} pins — see details below`);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message;
            toast.error(`Send failed: ${detail}`);
            setLastSendResult({ error: detail });
        } finally {
            setSending(false);
        }
    }, [packageId, sending]);

    const handleBulkApprove = useCallback(async () => {
        if (bulkApproving) return;
        setBulkApproving(true);
        try {
            const r = await adminBulkApproveImages(packageId);
            if (r.approved > 0) {
                toast.success(`Approved ${r.approved} image(s)${r.skipped ? ` · ${r.skipped} skipped` : ""}`);
            } else {
                toast.info("Nothing to approve — no images in 'generated' status.");
            }
            setReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(`Bulk approve failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBulkApproving(false);
        }
    }, [packageId, bulkApproving]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-bl-muted py-8">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading image gallery…
            </div>
        );
    }

    if (!data) return null;
    const { assets, summary } = data;
    const allMissing = assets.every((a) => a.generation_status === "pending");
    const anyMissing = assets.some((a) => a.generation_status !== "generated" && a.generation_status !== "approved");

    return (
        <div className="space-y-5" data-testid="images-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-semibold text-lg text-bl tracking-tight">
                        Image Gallery · {summary.total} assets
                    </h3>
                    <div className="text-xs text-bl-muted">
                        {summary.generated + summary.approved} generated · {summary.pending} pending · {summary.failed} failed
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => handleGenerateAll({ force: false })}
                        disabled={generating || progress?.status === "running" || !anyMissing}
                        className="bl-btn-primary text-xs gap-1.5"
                        data-testid="generate-missing-images-btn"
                    >
                        {generating || progress?.status === "running" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                        )}
                        {allMissing ? "Generate all 9 images" : "Generate missing"}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleGenerateAll({ force: true })}
                        disabled={generating || progress?.status === "running"}
                        className="bl-btn-ghost text-xs gap-1.5"
                        data-testid="regenerate-all-images-btn"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Regenerate all
                    </button>
                    <button
                        type="button"
                        onClick={handleBulkApprove}
                        disabled={
                            bulkApproving
                            || summary.generated === 0
                        }
                        title={
                            summary.generated === 0
                                ? "No images in 'generated' status to approve"
                                : `Approve all ${summary.generated} generated image(s) at once`
                        }
                        className="bl-btn-primary text-xs gap-1.5"
                        data-testid="bulk-approve-images-btn"
                    >
                        {bulkApproving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <CheckCheck className="h-3.5 w-3.5" />
                        )}
                        Bulk Approve
                        {summary.generated > 0 && (
                            <span className="ml-0.5 opacity-75">({summary.generated})</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={handleSendToPinterest}
                        disabled={
                            sending
                            || !metricoolStatus?.configured
                            || summary.generated + summary.approved < 9
                        }
                        title={
                            !metricoolStatus?.configured
                                ? "Configure Metricool in /admin/metricool first"
                                : summary.generated + summary.approved < 9
                                    ? "Generate all 9 images first"
                                    : "Schedule all 8 pins on Pinterest via Metricool"
                        }
                        className="bl-btn-primary text-xs gap-1.5"
                        data-testid="send-to-pinterest-btn"
                    >
                        {sending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Send className="h-3.5 w-3.5" />
                        )}
                        Send to Pinterest
                    </button>
                </div>
            </div>

            <ProgressBar progress={progress} />

            {/* Metricool config banner */}
            {metricoolStatus && !metricoolStatus.configured && (
                <div
                    className="bl-card p-3 border-l-4 border-l-[var(--bl-amber)] text-xs"
                    data-testid="metricool-not-configured-banner"
                >
                    <div className="font-semibold text-[var(--bl-amber)] flex items-center gap-1.5">
                        <Send className="h-4 w-4" /> Metricool not configured
                    </div>
                    <p className="text-bl-muted mt-1">
                        Add <code>METRICOOL_USER_TOKEN</code> + <code>METRICOOL_USER_ID</code> to
                        <code> backend/.env</code> and restart the backend, then map the 5
                        internal boards to Metricool Pinterest board IDs in
                        <code> /admin/metricool</code>.
                    </p>
                </div>
            )}
            {lastSendResult && (
                <div
                    className="bl-card p-3 text-xs"
                    data-testid="metricool-send-result"
                >
                    {lastSendResult.error ? (
                        <>
                            <div className="font-semibold text-[var(--bl-rust)] flex items-center gap-1.5">
                                <AlertCircle className="h-4 w-4" /> Send failed
                            </div>
                            <p className="text-bl-muted mt-1">{lastSendResult.error}</p>
                        </>
                    ) : (
                        <>
                            <div className={`font-semibold flex items-center gap-1.5 ${lastSendResult.ok ? "text-[var(--bl-green)]" : "text-[var(--bl-amber)]"}`}>
                                {lastSendResult.ok ? (
                                    <CheckCircle2 className="h-4 w-4" />
                                ) : (
                                    <AlertCircle className="h-4 w-4" />
                                )}
                                Scheduled {lastSendResult.scheduled} of {lastSendResult.total} pins on {lastSendResult.channel?.name || "Pinterest"}
                            </div>
                            <ul className="mt-2 space-y-1 text-bl-muted">
                                {(lastSendResult.results || []).map((r) => (
                                    <li key={r.key} className="flex items-center gap-2">
                                        <span className={`h-1.5 w-1.5 rounded-full ${r.scheduled ? "bg-[var(--bl-green)]" : r.already_scheduled ? "bg-amber-500" : "bg-[var(--bl-rust)]"}`} />
                                        <span className="font-medium text-bl">{r.label}</span>
                                        {r.scheduled ? (
                                            <span>· due {new Date(r.due_at).toLocaleDateString()}</span>
                                        ) : r.already_scheduled ? (
                                            <span data-testid={`pin-already-scheduled-${r.key}`}>· already scheduled{r.due_at ? ` — due ${new Date(r.due_at).toLocaleDateString()}` : ""}</span>
                                        ) : (
                                            <span>· {r.error}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}

            {summary.package_status === "image_generation_failed" && (
                <div className="bl-card p-3 border-l-4 border-l-[var(--bl-rust)] text-xs">
                    <div className="font-semibold text-[var(--bl-rust)] flex items-center gap-1.5">
                        <AlertCircle className="h-4 w-4" /> Image generation failed
                    </div>
                    <p className="text-bl-muted mt-1">
                        No image assets generated successfully. Check Nano Banana
                        availability + EMERGENT_LLM_KEY balance and click
                        &ldquo;Regenerate all&rdquo;.
                    </p>
                </div>
            )}

            {allMissing && !generating && progress?.status !== "running" && (
                <div className="bl-card p-4 text-center border-dashed border-2 border-bl/30">
                    <ImageIcon className="h-8 w-8 mx-auto text-bl-muted/60 mb-2" />
                    <h4 className="font-medium text-sm text-bl mb-1">No images generated yet</h4>
                    <p className="text-xs text-bl-muted mb-3">
                        Click <em>Generate all 9 images</em> to render the article infographic +
                        8 Pinterest pins via Gemini Nano Banana.
                    </p>
                    <button
                        type="button"
                        onClick={() => handleGenerateAll({ force: false })}
                        className="bl-btn-primary text-xs gap-1.5 mx-auto"
                        data-testid="empty-state-generate-btn"
                    >
                        <PlayCircle className="h-4 w-4" /> Generate all 9 images
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                {assets.map((asset) => {
                    const pinMeta = pinsByAssetType[asset.asset_type];
                    const angleKey = pinMeta?.key;
                    const overrideBoardKey = angleKey
                        ? (pinSettings?.pin_overrides || {})[angleKey]?.board_key
                        : null;
                    const defaultBoardKey = angleKey
                        ? (pinSettings?.defaults || {})[angleKey]
                        : null;
                    return (
                        <ImageCard
                            key={asset.asset_id || asset.asset_type}
                            asset={asset}
                            packageId={packageId}
                            onChange={reload}
                            pinMeta={pinMeta}
                            boards={pinSettings?.boards_catalog || metricoolStatus?.boards_catalog}
                            defaultBoardKey={defaultBoardKey}
                            currentBoardKey={overrideBoardKey}
                            onBoardChange={handlePinBoardChange}
                        />
                    );
                })}
            </div>
        </div>
    );
}
